import { DEFAULT_PLAYER_IMAGE } from 'constants/print';

const resolveNameParts = (firstNameOrPlayer, lastNameEn) => {
    if (firstNameOrPlayer != null && typeof firstNameOrPlayer === 'object') {
        return {
            first: firstNameOrPlayer.firstNameEn ?? firstNameOrPlayer.firstName ?? firstNameOrPlayer.firstname ?? '',
            last: firstNameOrPlayer.lastNameEn ?? firstNameOrPlayer.lastName ?? firstNameOrPlayer.lastname ?? ''
        };
    }

    return { first: firstNameOrPlayer, last: lastNameEn };
};

export const getPlayerNameParts = (firstNameOrPlayer, lastNameEn) => {
    const { first, last } = resolveNameParts(firstNameOrPlayer, lastNameEn);

    return { firstName: first, lastName: last };
};

/**
 * Build a display name from first/last strings or a player/profile object.
 * @param {string|object} firstNameOrPlayer
 * @param {string|object} [lastNameOrOptions]
 * @param {{ trim?: boolean }} [maybeOptions]
 */
export const getPlayerDisplayName = (firstNameOrPlayer, lastNameOrOptions, maybeOptions) => {
    const isPlayerObject = firstNameOrPlayer != null && typeof firstNameOrPlayer === 'object';
    const options = isPlayerObject
        ? lastNameOrOptions && typeof lastNameOrOptions === 'object'
            ? lastNameOrOptions
            : {}
        : maybeOptions && typeof maybeOptions === 'object'
        ? maybeOptions
        : {};
    const { trim = true } = options;
    const { first, last } = resolveNameParts(firstNameOrPlayer, isPlayerObject ? undefined : lastNameOrOptions);

    return [first, last]
        .map(part => {
            if (part == null || part === '') {
                return '';
            }
            const value = String(part);

            return trim ? value.trim() : value;
        })
        .filter(Boolean)
        .join(' ');
};

export const getPlayerInitials = (firstName, lastName, fallbackName = '') => {
    const first = String(firstName || '').trim();
    const last = String(lastName || '').trim();

    if (first || last) {
        return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }

    const parts = String(fallbackName || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

export const getEliteProspectId = entity =>
    entity?.eliteProspectPlayerId ?? entity?.eliteProspectsId ?? entity?.eliteProspectId;

export const resolveEliteProspectId = profile => getEliteProspectId(profile) ?? null;

export const normalizeScoutPlayerProfile = profile => {
    if (!profile) return null;

    const id = profile.playerProfileId ?? profile.id;
    if (id == null) return null;

    const { lastTeam: _lastTeam, lastTeamId: _lastTeamId, ...rest } = profile;
    const lastTeam = profile.lastTeam ?? profile.params?.teamName;
    const lastTeamId = profile.lastTeamId ?? profile.params?.teamId;

    return {
        ...rest,
        id,
        playerProfileId: id,
        ...(lastTeam ? { lastTeam } : {}),
        ...(lastTeamId != null && lastTeamId !== '' ? { lastTeamId } : {})
    };
};

export const resolvePlayerPhotoUrl = photoHref => {
    const url = String(photoHref || '').trim();
    if (!url || url === DEFAULT_PLAYER_IMAGE || url.includes('default.player.png')) {
        return null;
    }

    return url;
};

export const parseEliteProspectPhotos = data => {
    if (!data) return {};

    if (Array.isArray(data)) {
        return data.reduce((acc, item) => {
            if (!item || typeof item !== 'object') return acc;

            const id = getEliteProspectId(item);
            const href = item.photoHref;

            if (id != null && href && String(href).trim()) {
                acc[String(id)] = String(href).trim();
            }

            return acc;
        }, {});
    }

    if (typeof data !== 'object') return {};

    if (data.photos && typeof data.photos === 'object') {
        return parseEliteProspectPhotos(data.photos);
    }

    return Object.entries(data).reduce((acc, [key, value]) => {
        if (typeof value === 'string' && value.trim()) {
            acc[String(key)] = value.trim();

            return acc;
        }

        if (value && typeof value === 'object' && value.photoHref && String(value.photoHref).trim()) {
            const id = getEliteProspectId(value) ?? key;
            acc[String(id)] = String(value.photoHref).trim();
        }

        return acc;
    }, {});
};

/** Prefer photoHref on the profile; fall back to Elite Prospects batch lookup map. */
export const resolveScoutPlayerPhotoUrl = (player, photosByEliteProspectId = {}) => {
    const directPhoto = resolvePlayerPhotoUrl(player?.photoHref);
    if (directPhoto) return directPhoto;

    const eliteProspectsId = getEliteProspectId(player);
    if (!eliteProspectsId) return null;

    return photosByEliteProspectId[String(eliteProspectsId)] || null;
};
